// TODO Sure are lots of anonymous functions and for loops in here...
import { navigate } from "gatsby";
import PropTypes from "prop-types";
import React, { createElement, Fragment, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";

import { Form } from "@racepointenergy/gatsby-theme-sitespark/src/components/Html/form";
import { getSize } from "@racepointenergy/gatsby-theme-sitespark/src/components/Theme/utils";
import { useJsEnabled } from "@racepointenergy/gatsby-theme-sitespark/src/hooks";

import {
  Button as CallToActionButton,
  Link as CallToActionLink,
} from "components/CallToAction";

import { handleFormSubmit } from "utils/handle-form-submit";

import { BasicInfoForm } from "./components/basic-info-form";
import { ElectricalDetailsForm } from "./components/electrical-details-form";
import { DesiredComponentsForm } from "./components/desired-components-form";
import { DesiredSystemForm } from "./components/desired-system-form";
import { MajorElectricalLoadsForm } from "./components/major-electrical-loads-form";
import { DocumentsForm } from "./components/documents-form";
import { PowerMove } from "./components/power-move";

import {
  baseFactory,
  Section,
  sectionPaddingCss,
  sectionViewCss,
} from "./styles";

const StyledForm = styled(Form)`
  ${sectionPaddingCss}
  padding-top: 0px;
  @media screen and (min-width: ${getSize("mobile480")}) {
    padding-top: 0px;
  }
  @media screen and (min-width: ${getSize("tablet720")}) {
    padding-top: 0px;
  }
  @media screen and (min-width: ${getSize("tablet960")}) {
    padding-top: 0px;
  }
  @media screen and (min-width: ${getSize("desktop1200")}) {
    padding-top: 0px;
  }
  @media screen and (min-width: ${getSize("desktop1920")}) {
    padding-top: 0px;
  }
`;

const FormsList = styled.ol`
  ${sectionViewCss}
`;

const PaginateControlNav = styled.nav`
  display: grid;
  gap: 6px;
  grid-auto-columns: 44px;
  grid-auto-flow: column;
  justify-content: center;
  margin: 30px 0px;
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const PaginateControl = styled(CallToActionLink)`
  ${({
    theme: {
      colors: { lightGrey },
    },
  }) => `
    border: 2px solid ${lightGrey};
    color: ${lightGrey};
    `}
  border-radius: 50%;
  font-size: 20px;
  height: 44px;
  min-width: 0px;
  padding: 0px;
  width: 44px;
  display: grid;
  place-items: center center;
  @media screen and (min-width: ${getSize("tablet720")}) {
    padding: 0;
  }
`;

const NoJsPaginateControl = styled(PaginateControl)`
  ${({
    theme: {
      colors: { blueGrey },
    },
  }) => css`
    animation: 0.25s ${fadeIn} ease-out;
    border: 2px solid ${blueGrey};
    color: ${blueGrey};
  `}
`;

const ActivePaginateControl = styled(NoJsPaginateControl)`
  animation: unset;
`;

const CallToActionWrapper = styled.nav`
  bottom: 0;
  display: grid;
  gap: 21px;
  grid-auto-flow: column;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 0;
  text-align: center;
`;

const responsiveCtaStyles = css`
  width: 135px;
  min-width: 135px;
  @media screen and (min-width: ${getSize("desktop1920")}) {
    border-width: 2px;
    font-size: 18px;
    padding: 20px 54px;
    width: 171px;
  }
`;

const NextPrevControl = styled(CallToActionLink)`
  ${responsiveCtaStyles}
`;

const SubmitButton = styled(CallToActionButton)`
  ${responsiveCtaStyles} /* .submitting {} */
`;

const Alert = styled.label`
  display: block;
  padding: 8px;
  margin: 0 0 19px 0;
  text-align: center;
  ${({
    error,
    theme: {
      colors: { red, pink },
    },
  }) => `
    background-color: ${pink};
    color: ${red};
    visibility: ${error ? "initial" : "hidden"};
  `}
  @media screen and (min-width: ${getSize("tablet720")}) {
    padding: 14px 8px;
  }
  @media screen and (min-width: ${getSize("tablet960")}) {
    padding: 17px 8px;
  }
`;

// object keys become id attributes
const forms = Object.entries({
  basicInfo: BasicInfoForm,
  electricalDetails: ElectricalDetailsForm,
  desiredComponents: DesiredComponentsForm,
  desiredSystem: DesiredSystemForm,
  majorElectricalLoads: MajorElectricalLoadsForm,
  documents: DocumentsForm,
});

const getNextId = index =>
  index + 1 < forms.length ? forms[index + 1][0] : undefined;
const getPrevId = index => (index > 0 ? forms[index - 1][0] : undefined);

export function GetAPreliminaryProposal({ action, hash }) {
  const formRef = useRef();
  const jsEnabled = useJsEnabled();
  const [showError, setShowError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  return (
    <Fragment>
      <PowerMove />
      <StyledForm
        action={action}
        encType="multipart/form-data"
        method="post"
        onSubmit={submitEvent => {
          setShowError(false);
          setSubmitting(true);
          handleFormSubmit(
            function onSuccess() {
              setSubmitting(false);
              navigate("/get-a-preliminary-proposal/success#thankYou");
            },
            function onError() {
              setSubmitting(false);
              setShowError(true);
            }
          )(submitEvent);
        }}
        ref={formRef}
      >
        <Alert htmlFor="submit" error={showError}>
          There was an error submitting your request. Please verify your data
          and try again.
        </Alert>
        <FormsList>
          {forms.map(([id, FormPartialComponent], index) => {
            const nextId = getNextId(index);
            const prevId = getPrevId(index);
            const renderSubmit = index + 1 === forms.length;
            return (
              <Section id={id} key={id}>
                {createElement(FormPartialComponent, {
                  base: baseFactory(id),
                  id,
                })}
                <CallToActionWrapper>
                  {prevId && (
                    <NextPrevControl to={`#${prevId}`}>back</NextPrevControl>
                  )}
                  {nextId && (
                    <NextPrevControl
                      onClick={function validateFormPartialBeforeNavigate(e) {
                        const formControls = formRef.current.elements;
                        for (let i = 0; i < formControls.length; i++) {
                          // for each form control
                          if (
                            formControls[i].getAttribute("name").startsWith(id)
                          ) {
                            // form control is part of current form partial
                            if (!formControls[i].reportValidity()) {
                              // control is invalid
                              e.preventDefault(); // prevent navigation :(
                              return; // bailout
                            }
                          }
                        }
                      }}
                      to={`#${nextId}`}
                    >
                      next
                    </NextPrevControl>
                  )}
                  {renderSubmit && (
                    <SubmitButton
                      disabled={submitting}
                      name="submit"
                      type="submit"
                    >
                      submit
                    </SubmitButton>
                  )}
                </CallToActionWrapper>
              </Section>
            );
          })}
        </FormsList>
        <PaginateControlNav>
          {forms.map(([id], index) =>
            createElement(
              !jsEnabled // don't use active hash link styles without JS enabled
                ? NoJsPaginateControl // all buttons start grey and fade in to blue
                : hash === `#${id}` || (hash === "" && index === 0) // is hash active?
                ? ActivePaginateControl // active is blue
                : PaginateControl, // inactive is grey
              {
                key: id,
                onClick: function validateAllPreviousFormControls(e) {
                  const formControls = formRef.current.elements;
                  if (index <= 0) return;
                  for (const [previousFormPartialId] of forms.slice(0, index)) {
                    // all previous form partials
                    for (let i = 0; i < formControls.length; i++) {
                      // all controls in form
                      if (
                        formControls[i]
                          .getAttribute("name")
                          .startsWith(previousFormPartialId)
                      ) {
                        // control is part of current form partial
                        if (!formControls[i].reportValidity()) {
                          // form control is invalid
                          e.preventDefault(); // prevent navigation
                          return; // bailout
                        }
                      }
                    }
                  }
                },
                to: `#${id}`,
              },
              index + 1
            )
          )}
        </PaginateControlNav>
      </StyledForm>
    </Fragment>
  );
}

GetAPreliminaryProposal.propTypes = {
  action: PropTypes.string,
  hash: PropTypes.string,
};
